import { Socket as SocketClient, connect as connectSocket } from "socket.io-client";
import { HandleError } from "../utils";
import { APIClientServices } from "./APIClientServices";
import { UserServices } from "./UserServices";
import { uuidv4, EventEmitter } from "@ismael1361/utils";
import { AccountType } from "shared/types";
import { AccountModel, IAccountModel } from "shared/model";

const state = {
	CONNECTION_STATE_DISCONNECTED: "disconnected",
	CONNECTION_STATE_CONNECTING: "connecting",
	CONNECTION_STATE_CONNECTED: "connected",
	CONNECTION_STATE_DISCONNECTING: "disconnecting",
} as const;

type STATE = (typeof state)[keyof typeof state];

const onAuthStateChanged = (callback: () => void) => {
	setTimeout(() => {
		try {
			UserServices.onAuthStateChanged(callback);
		} catch {
			onAuthStateChanged(callback);
		}
	}, 1000);
};

/**
 * Gerencia a conexão WebSocket com o servidor utilizando a biblioteca `socket.io-client`.
 *
 * Esta classe estende `EventEmitter` para fornecer um sistema de eventos tipado, permitindo
 * a comunicação em tempo real entre o cliente e o servidor. Ela gerencia automaticamente
 * a autenticação via token, reconexão em caso de falhas e o ciclo de vida da conexão.
 *
 * @extends EventEmitter
 *
 * @example
 * ```typescript
 * // Inicializa o serviço com a URL do servidor
 * const socketService = new WebsocketServices("http://api.exemplo.com");
 *
 * // Escuta o evento de conexão
 * socketService.on("connect", () => {
 *   console.log("Conectado ao servidor WebSocket!");
 * });
 *
 * // Escuta mensagens específicas do servidor
 * socketService.onReceive("nova_notificacao", (data) => {
 *   console.log("Notificação recebida:", data);
 * });
 *
 * // Envia uma requisição e aguarda a resposta
 * try {
 *   const response = await socketService.request("obter_dados", { id: 123 });
 *   console.log("Dados recebidos:", response);
 * } catch (error) {
 *   console.error("Erro na requisição:", error);
 * }
 * ```
 */
export class WebsocketServices extends EventEmitter<{
	[path: `/${string}`]: [data: any];
	connect: [];
	disconnect: [];
	reconnecting: [];
	reconnect: [];
	reconnect_failed: [];
	receive: [event: string, data: any];
}> {
	/**
	 * Token de acesso (Bearer Token) utilizado para autenticação na conexão WebSocket.
	 * É atualizado automaticamente a partir do `UserServices`.
	 * @private
	 */
	private __accessToken__: string | undefined;

	/**
	 * Instância do cliente `Socket` da biblioteca `socket.io-client`.
	 * Responsável pela comunicação de baixo nível com o servidor.
	 * @private
	 */
	private io: SocketClient | undefined;

	/**
	 * Estado atual da conexão WebSocket.
	 * Pode ser: 'disconnected', 'connecting', 'connected' ou 'disconnecting'.
	 * @private
	 * @default "disconnected"
	 */
	private __connectionState__: STATE = state.CONNECTION_STATE_DISCONNECTED;

	/**
	 * Mapa para armazenar referências de callbacks registrados para eventos `receive`.
	 * Utilizado para gerenciar a remoção correta de listeners quando necessário.
	 * @private
	 */
	private __callbacks__: Map<Function, Function> = new Map();

	/**
	 * Cria uma nova instância do serviço de WebSocket.
	 *
	 * Configura o ouvinte de autenticação para reconectar automaticamente
	 * quando o estado de autenticação do usuário mudar.
	 *
	 * @param baseURL URL base do servidor WebSocket (ex: `http://localhost:3000`).
	 */
	constructor(public baseURL: string) {
		super();

		onAuthStateChanged(() => {
			this.reconnect();
		});

		this.reconnect();
	}

	/**
	 * Obtém o token de acesso atual usado na conexão.
	 *
	 * @returns O token `Bearer` atual ou `undefined` se não houver.
	 */
	get accessToken() {
		return this.__accessToken__;
	}

	/**
	 * Obtém o estado atual da conexão WebSocket.
	 *
	 * @returns O estado da conexão (ex: 'connected', 'disconnected', 'connecting').
	 */
	get connectionState() {
		return this.__connectionState__;
	}

	/**
	 * Define o estado da conexão e atualiza o status de prontidão (`prepared`).
	 *
	 * Quando o estado é definido como `connected`, o serviço é marcado como "preparado",
	 * o que permite que eventos enfileirados em `ready()` sejam executados.
	 *
	 * @param value Novo estado da conexão.
	 * @private
	 */
	set connectionState(value: STATE) {
		this.__connectionState__ = value;
		this.prepared = value === state.CONNECTION_STATE_CONNECTED;
	}

	/**
	 * Verifica se o WebSocket está atualmente conectado.
	 *
	 * @returns `true` se o estado for `connected`, caso contrário `false`.
	 *
	 * @example
	 * if (socketService.isConnected) {
	 *   console.log("Estamos online!");
	 * }
	 */
	get isConnected() {
		return this.connectionState === state.CONNECTION_STATE_CONNECTED;
	}

	/**
	 * Verifica se o WebSocket está tentando estabelecer conexão.
	 *
	 * @returns `true` se o estado for `connecting`, caso contrário `false`.
	 */
	get isConnecting() {
		return this.connectionState === state.CONNECTION_STATE_CONNECTING;
	}

	/**
	 * Inicia o processo de conexão com o servidor WebSocket.
	 *
	 * Verifica se há um token de autenticação disponível. Se não houver, a conexão é abortada.
	 * Se já estiver conectado ou conectando, a promessa é resolvida imediatamente.
	 * Configura todos os listeners de eventos do socket (connect, disconnect, receive, etc.).
	 *
	 * @returns Uma `Promise` que resolve quando a conexão inicial é estabelecida ou se a tentativa for concluída.
	 */
	async connect() {
		await new Promise<void>(async (resolve, reject) => {
			const accessToken = UserServices.currentToken;

			if (!accessToken || accessToken.trim() === "") {
				this.__accessToken__ = undefined;
				return resolve();
			}

			this.__accessToken__ = accessToken;

			let resolved = false;
			if (this.connectionState === state.CONNECTION_STATE_DISCONNECTED) {
				this.connectionState = state.CONNECTION_STATE_CONNECTING;

				this.io = connectSocket(this.baseURL.replace(/^http(s?)/gi, "ws$1"), {
					// Use default socket.io connection settings:
					path: `/socket.io`,
					autoConnect: true,
					reconnectionDelay: 5000,
					reconnectionDelayMax: 5000,
					timeout: 20000,
					randomizationFactor: 0.5,
					transports: ["websocket"], // Override default setting of ['polling', 'websocket']
					query: {
						token: accessToken,
					},
					extraHeaders: {
						Authorization: accessToken ? `Bearer ${accessToken}` : "",
					},
				});

				this.io.on("connect", () => {
					this.connectionState = state.CONNECTION_STATE_CONNECTED;
					this.emit("connect");

					if (!resolved) {
						resolved = true;
						resolve();
					}
				});

				this.io.on("disconnect", () => {
					this.connectionState = state.CONNECTION_STATE_DISCONNECTING;
					this.emit("disconnect");
				});

				this.io.on("connect_error", (error) => {
					this.connectionState = state.CONNECTION_STATE_DISCONNECTED;
					this.emit("disconnect");
					setTimeout(() => {
						this.reconnect();
					}, 1000 * 10); // 10 seconds
				});

				this.io.on("reconnecting", () => {
					this.connectionState = state.CONNECTION_STATE_CONNECTING;
					this.emit("reconnecting");
				});

				this.io.on("reconnect", () => {
					this.connectionState = state.CONNECTION_STATE_CONNECTED;
					this.emit("reconnect");
				});

				this.io.on("reconnect_failed", () => {
					this.connectionState = state.CONNECTION_STATE_DISCONNECTED;
					this.emit("reconnect_failed");
					setTimeout(() => {
						this.reconnect();
					}, 1000 * 10); // 10 seconds
				});

				this.io.on("receive", (receive: { event: string; data: any }) => {
					this.emit("receive", receive.event, receive.data);
				});

				this.io.on("path", (receive: { path: `/${string}`; data: any }) => {
					this.emit(receive.path, receive.data);
				});

				return;
			}

			resolve();
		});
	}

	/**
	 * Encerra a conexão com o servidor WebSocket.
	 *
	 * Define o estado como desconectado e limpa a referência do cliente socket.
	 *
	 * @returns Uma `Promise` que resolve após a desconexão.
	 *
	 * @example
	 * await socketService.disconnect();
	 * console.log("Desconectado manualmente.");
	 */
	async disconnect() {
		if (this.connectionState === state.CONNECTION_STATE_CONNECTED) {
			this.connectionState = state.CONNECTION_STATE_DISCONNECTED;
			this.io?.disconnect();
			this.io = undefined;
		}
	}

	/**
	 * Força uma tentativa de reconexão se o serviço estiver desconectado.
	 *
	 * Este método desconecta qualquer instância anterior antes de tentar conectar novamente.
	 * Útil para reiniciar a conexão quando as credenciais mudam ou após um erro fatal.
	 *
	 * @returns Uma `Promise` que resolve após o início da tentativa de conexão.
	 */
	async reconnect() {
		if (this.connectionState === state.CONNECTION_STATE_DISCONNECTED) {
			this.io?.disconnect();
			this.io = undefined;
			this.connect();
		}
	}

	/**
	 * Registra um ouvinte (listener) para mensagens recebidas com um evento específico.
	 *
	 * O callback será executado toda vez que o servidor enviar uma mensagem com o nome do evento especificado.
	 * Internamente, escuta o evento genérico "receive" e filtra pelo nome do evento.
	 *
	 * @template D Tipo de dado esperado no corpo da mensagem.
	 * @param event Nome do evento a ser escutado (ex: "notification").
	 * @param callback Função a ser executada quando a mensagem chegar. Recebe os dados da mensagem.
	 * @returns Uma função para remover este listener (opcionalmente usada, mas prefira `offReceive`).
	 *
	 * @example
	 * interface Notification { title: string; }
	 *
	 * const handleNotification = (data: Notification) => {
	 *   console.log("Notificação:", data.title);
	 * };
	 *
	 * socketService.onReceive<Notification>("notification", handleNotification);
	 */
	onReceive<D = any>(event: string, callback: (data: D) => void) {
		const c = (e: any, data: any) => {
			if (e === event) {
				callback(data);
			}
		};
		this.__callbacks__.set(callback, c);
		const { stop } = this.on("receive", c);
		const self = this;

		return {
			stop() {
				self.__callbacks__.delete(callback);
				stop();
			},
			remove() {
				this.stop();
			},
		};
	}

	/**
	 * Registra um ouvinte para um evento específico que será executado apenas uma vez.
	 *
	 * Após a primeira execução, o listener é removido automaticamente.
	 *
	 * @template D Tipo de dado esperado no corpo da mensagem.
	 * @param event Nome do evento a ser escutado.
	 * @param callback Função a ser executada uma única vez quando a mensagem chegar.
	 * @returns Referência ao listener interno.
	 *
	 * @example
	 * socketService.onceReceive("init_config", (config) => {
	 *   console.log("Configuração inicial recebida:", config);
	 * });
	 */
	onceReceive<D = any>(event: string, callback: (data: D) => void) {
		const c = (e: any, data: any) => {
			if (e === event) {
				callback(data);
			}
		};
		this.__callbacks__.set(callback, c);
		return this.once("receive", c);
	}

	/**
	 * Remove um ouvinte registrado anteriormente com `onReceive`.
	 *
	 * É necessário passar a mesma referência da função de callback usada no registro.
	 *
	 * @param event Nome do evento (DEVE coincidir com o usado em `onReceive`).
	 * @param callback A função de callback original que foi registrada.
	 * @returns O resultado da operação de remoção interna.
	 *
	 * @example
	 * socketService.offReceive("notification", handleNotification);
	 */
	offReceive(event: string, callback: (data: any) => void) {
		const c = this.__callbacks__.get(callback);
		if (!c) {
			return;
		}
		this.__callbacks__.delete(callback);
		return this.off("receive", c as any);
	}

	/**
	 * Remove um ouvinte registrado anteriormente com `onceReceive` antes que ele seja executado.
	 *
	 * @param event Nome do evento.
	 * @param callback A função de callback original.
	 * @returns O resultado da remoção.
	 */
	offOnceReceive(event: string, callback: (data: any) => void) {
		const c = this.__callbacks__.get(callback);
		if (!c) {
			return;
		}
		this.__callbacks__.delete(callback);
		return this.offOnce("receive", c as any);
	}

	/**
	 * Envia uma requisição ao servidor WebSocket e aguarda uma resposta (modelo Request/Response).
	 *
	 * Este método implementa um padrão de timeout e retry. Se o servidor não responder dentro
	 * de 5 segundos, a requisição é reenviada (até 3 tentativas no total).
	 *
	 * Utiliza um ID único (`request_id`) para correlacionar a requisição com a resposta.
	 *
	 * @template R Tipo esperado da resposta.
	 * @param event Nome do evento da requisição.
	 * @param data Dados a serem enviados no corpo da requisição.
	 * @returns Uma `Promise` que resolve com os dados da resposta do servidor.
	 * @throws {HandleError} Se houver erro de conexão, timeout após retentativas ou se o servidor retornar erro.
	 *
	 * @example
	 * try {
	 *   const user = await socketService.request<User>("get_user", { id: 1 });
	 *   console.log("Usuário:", user.name);
	 * } catch (err) {
	 *   console.error("Erro ao buscar usuário:", err);
	 * }
	 */
	async request<R = any>(event: string, data: any = {}): Promise<R> {
		return await this.ready(
			async () =>
				await new Promise((resolve, reject) => {
					const checkConnection = (callback: () => any) => {
						if (!this.io?.connected) {
							return reject(new HandleError("Connection is not established.", "CONNECTION_NOT_ESTABLISHED"));
						}
						callback();
					};

					const requestId = uuidv4();

					const request = { event, request_id: requestId, data };

					checkConnection(() => {
						let timeout: NodeJS.Timeout;

						const handle = (response: { status: "success" | "error"; data: any; request_id: string }) => {
							if (response.request_id === requestId) {
								clearTimeout(timeout);
								this.io?.off("result", handle);
								if (response.status === "error") {
									reject(new HandleError(response.data.message, response.data.name, response.data.cause));
								} else {
									resolve(response.data);
								}
							}
						};

						const send = (retry = 0) => {
							checkConnection(() => {
								this.io?.emit("request", request);
								timeout = setTimeout(() => {
									if (retry < 2) {
										return send(retry + 1);
									}
									this.io?.off("result", handle);
									const err = new HandleError(
										`Server did not respond to "${event}" request after ${retry + 1} tries`,
										"REQUEST_TIMEOUT",
									);
									reject(err);
								}, 5000);
							});
						};

						this.io?.on("result", handle);
						send();
					});
				}),
		);
	}

	/**
	 * Envia uma mensagem unidirecional para o servidor (Fire-and-Forget).
	 *
	 * Não aguarda resposta do servidor. Envolve os dados em um objeto `{ event, data }`
	 * e emite o evento genérico "receive".
	 *
	 * @template D Tipo dos dados a serem enviados.
	 * @param event Nome do evento.
	 * @param data Dados a serem enviados.
	 * @returns Uma `Promise` que resolve assim que o envio é agendado (pode aguardar conexão estar pronta).
	 *
	 * @example
	 * socketService.sendData("log_action", { action: "button_click" });
	 */
	async sendData<D = any>(event: string, data: D): Promise<void> {
		return this.ready(async () => {
			this.io?.emit("receive", { event, data });
		});
	}

	async sendByPath<D = any>(path: `/${string}`, data: D) {
		return this.ready(async () => {
			this.io?.emit("path", { path, data });
		});
	}

	async onPath<D = any>(path: `/${string}`, callback: (data: D) => void) {
		const c = (data: any) => {
			callback(data);
		};

		this.__callbacks__.set(callback, c);
		const { stop } = this.on(path, c);
		const self = this;

		return {
			stop() {
				self.__callbacks__.delete(callback);
				stop();
			},
			remove() {
				this.stop();
			},
		};
	}

	async oncePath<D = any>(path: `/${string}`, callback: (data: D) => void) {
		const c = (data: any) => {
			callback(data);
		};

		this.__callbacks__.set(callback, c);
		return this.once(path, c);
	}

	offPath(path: `/${string}`, callback: (data: any) => void) {
		const c = this.__callbacks__.get(callback);
		if (!c) {
			return;
		}

		this.__callbacks__.delete(callback);
		return this.off(path, c as any);
	}

	offOncePath(path: `/${string}`, callback: (data: any) => void) {
		const c = this.__callbacks__.get(callback);
		if (!c) {
			return;
		}

		this.__callbacks__.delete(callback);
		return this.offOnce(path, c as any);
	}
}

/**
 * Instância singleton do serviço WebSocket para uso em toda a aplicação.
 * Configurada com a URL da API definida em `APIClientServices`.
 * @example
 * import { WSServices } from "./WSServices";
 *
 * WSServices.on("connect", () => {
 *   console.log("Conectado ao WebSocket!");
 * });
 */
// export const WSServices = new WebsocketServices(APIClientServices.API_URL);
export const WSServices = new WebsocketServices(process.env.REACT_APP_WS_URL || APIClientServices.API_URL);

WSServices.onReceive<IAccountModel>("USER-UPDATE", (user) => {
	if (!!UserServices.currentUser && !!UserServices.currentToken) {
		UserServices.currentUser.clear(user);
	}
});
